import { Component, ComponentInterface, h, Host, JSX, Listen, Prop, Element } from '@stencil/core';
import { SharedDirectionService } from '../../global/init-services';
import { getDataOnFromTheme, WorkbenchTheme } from '../../shared/themes';

@Component({
  tag: 'wb-progress',
  styleUrl: 'progress.scss',
  shadow: true,
})
export class ProgressComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /** A percentage between 0 and 100 or 'indeterminate' */
  @Prop({ reflect: true })
  public value: number | 'indeterminate' = 0;

  /** If present, displays compact variant */
  @Prop({ reflect: true })
  public compact: boolean = false;

  /** If present, displays variant with border radius applied */
  @Prop({ reflect: true })
  public rounded: boolean = false;

  /**
   * The colour scheme
   * @deprecated Use `data-on` attribute instead
   */
  @Prop({ reflect: true })
  public theme?: WorkbenchTheme;

  /** Displays horizontal or vertical layout */
  @Prop({ reflect: true })
  public direction: 'horizontal' | 'vertical' = 'horizontal';

  /** aria-label for the progress element */
  @Prop()
  public progressAriaLabel: string = '';

  /** aria-valuetext for indeterminate progress element */
  @Prop()
  public indeterminateAriaValueText: string = 'processing';

  /** @internal if present sets the direction of the element to rtl */
  @Prop({ reflect: true, mutable: true })
  public isRtl: boolean = false;

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection() {
    this.isRtl = SharedDirectionService.isRtl;
  }

  public connectedCallback() {
    this.updateDirection();
  }

  public render(): JSX.Element {
    const sizeProperty = this.direction === 'horizontal' ? 'width' : 'height';

    return (
      <Host //
        class="wb-progress"
        role="progressbar"
        aria-live="polite"
        aria-label={this.progressAriaLabel}
        aria-busy="true"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow={this.value === 'indeterminate' ? 0 : this.value}
        aria-valuetext={this.value === 'indeterminate' ? this.indeterminateAriaValueText : null}
        data-on={getDataOnFromTheme(this.el, this.theme)}
      >
        {this.value === 'indeterminate' ? ( //
          <div class="track" />
        ) : (
          <div class="track" style={{ [sizeProperty]: `${this.value}%` }} />
        )}
      </Host>
    );
  }
}
